import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  MarkEmailReadOutlined,
} from '@mui/icons-material';
import { HTTPInstance } from '../../../core/http';

export default function UnsubscribePage() {
  const { palette } = useTheme();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'done' | 'error'>('loading');

  useEffect(() => {
    const token = params.get('token');

    if (!token) {
      setStatus('error');
      return;
    }

    HTTPInstance.get('/marketing/unsubscribe', { params: { token } })
      .then(() => setStatus('done'))
      .catch(() => setStatus('error'));
  }, [params]);

  const isLoading = status === 'loading';
  const isSuccess = status === 'done';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        backgroundColor: palette.background.default,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            backgroundColor: palette.background.paper,
          }}
        >
          <Box
            sx={{
              backgroundColor: palette.primary.main,
              px: 3,
              py: 2.5,
              color: palette.primary.contrastText,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <MarkEmailReadOutlined
                sx={{ color: palette.primary.contrastText }}
              />
              <Typography
                variant="h6"
                fontWeight={700}
                color={palette.primary.contrastText}
              >
                Email preferences
              </Typography>
            </Stack>
          </Box>

          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {isLoading && (
              <Stack alignItems="center" spacing={2.5} sx={{ py: 2 }}>
                <CircularProgress size={38} />
                <Typography
                  variant="h5"
                  fontWeight={700}
                  textAlign="center"
                  color={palette.text.primary}
                >
                  Processing your request...
                </Typography>
                <Typography
                  variant="body2"
                  color={palette.text.secondary}
                  textAlign="center"
                >
                  Please wait while we update your marketing preferences.
                </Typography>
              </Stack>
            )}

            {!isLoading && isSuccess && (
              <Stack spacing={2.5} alignItems="center" sx={{ py: 1 }}>
                <CheckCircleOutline
                  color="success"
                  sx={{ fontSize: 52, display: 'block' }}
                />
                <Typography
                  variant="h4"
                  fontWeight={700}
                  textAlign="center"
                  color={palette.text.primary}
                >
                  You’re unsubscribed.
                </Typography>
                <Typography
                  variant="body1"
                  color={palette.text.secondary}
                  textAlign="center"
                >
                  You will no longer receive marketing emails from us.
                </Typography>
                <Button
                  component={Link}
                  to="/"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 1, borderRadius: 999 }}
                >
                  Back to homepage
                </Button>
              </Stack>
            )}

            {!isLoading && !isSuccess && (
              <Stack spacing={2.5} alignItems="center" sx={{ py: 1 }}>
                <ErrorOutline
                  color="error"
                  sx={{ fontSize: 52, display: 'block' }}
                />
                <Typography
                  variant="h4"
                  fontWeight={700}
                  textAlign="center"
                  color={palette.text.primary}
                >
                  This link is invalid.
                </Typography>
                <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
                  The unsubscribe link may be expired, already used, or
                  malformed.
                </Alert>
                <Button
                  component={Link}
                  to="/"
                  variant="outlined"
                  color="primary"
                  size="large"
                  sx={{ borderRadius: 999 }}
                >
                  Return home
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
